"use client";

import { Suspense, useActionState, useEffect, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { signupAction } from "@/app/actions/auth";
import { GoogleButton } from "@/components/google-button";
import { BrandMark } from "@/components/brand";

// Where we stash an inbound referral code so it survives the buyer wandering
// off to the home page and back. The `?ref=` param only exists on the first
// hop from the share link; after that this is the source of truth.
const REF_STORAGE_KEY = "clearline_ref";

function normalizeRef(raw: string): string {
  return raw.trim().toUpperCase();
}

function Logo() {
  return (
    <Link href="/" className="flex items-center" aria-label="Clearline home">
      <BrandMark height={26} priority />
    </Link>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#080808]" />}>
      <SignupForm />
    </Suspense>
  );
}

function SignupForm() {
  const searchParams = useSearchParams();
  const next = searchParams.get("next") ?? "/";
  const urlRef = searchParams.get("ref") ?? "";

  // The effective referral code: whatever's in the URL, else whatever we
  // stashed on a previous visit. `referrerName` is set once the code is
  // confirmed against a real referrer so we can name them in the banner.
  const [ref, setRef] = useState("");
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [referralValid, setReferralValid] = useState(false);

  useEffect(() => {
    let stored = "";
    try {
      stored = window.localStorage.getItem(REF_STORAGE_KEY) ?? "";
    } catch {
      // localStorage can throw in private-mode/blocked contexts; fall back to
      // the URL param only.
    }
    const code = normalizeRef(urlRef || stored);
    setRef(code);
    if (urlRef) {
      try {
        window.localStorage.setItem(REF_STORAGE_KEY, code);
      } catch {
        // Non-fatal: the hidden input still carries the code for this session.
      }
    }
  }, [urlRef]);

  // Confirm the code points at a real referrer before promising anything. An
  // unknown/typo'd code shows no banner (and gets cleared so it stops
  // following the buyer around).
  useEffect(() => {
    if (!ref) {
      setReferralValid(false);
      setReferrerName(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/referral/resolve?code=${encodeURIComponent(ref)}`)
      .then((r) => r.json())
      .then((data: { valid?: boolean; referrerName?: string | null }) => {
        if (cancelled) return;
        if (data.valid) {
          setReferralValid(true);
          setReferrerName(data.referrerName ?? null);
        } else {
          setReferralValid(false);
          setReferrerName(null);
          try {
            window.localStorage.removeItem(REF_STORAGE_KEY);
          } catch {
            // ignore
          }
        }
      })
      .catch(() => {
        // Network hiccup: leave the banner hidden rather than making a promise
        // we can't stand behind. The code still rides along in the hidden input.
      });
    return () => {
      cancelled = true;
    };
  }, [ref]);

  const [state, formAction] = useActionState(signupAction, undefined);

  return (
    <div className="cl-scope">
      {/* The cl-* tokens and display/mono fonts from the Base44 build are not
          registered in this project's Tailwind theme, so we scope them here
          with the exact hex values from Base44's compiled CSS. */}
      <style>{authTokens}</style>

      <main className="min-h-screen bg-cl-bg flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-10">
            <Logo />
          </div>

          <h1 className="font-display text-3xl text-cl-text tracking-tight text-center mb-2">
            Create your account
          </h1>
          <p className="text-cl-muted text-sm text-center mb-8">
            Join the wholesale hub and start moving lots.
          </p>

          {referralValid ? (
            <div className="flex items-start gap-3 bg-cl-green-soft border border-cl-green rounded-2xl px-5 py-4 mb-6">
              <CheckSeal />
              <div>
                <p className="text-cl-green text-sm font-semibold">
                  {referrerName
                    ? `${referrerName} referred you`
                    : "You've been referred"}
                </p>
                <p className="text-cl-muted text-sm mt-0.5">
                  Create your account to claim $50,000 in free spending power.
                </p>
              </div>
            </div>
          ) : null}

          <div className="bg-cl-surface border border-cl-border rounded-2xl p-8">
            <GoogleButton next={next} refCode={ref} />

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-cl-border" />
              <span className="text-[10px] font-mono text-cl-muted uppercase tracking-widest">
                or
              </span>
              <div className="h-px flex-1 bg-cl-border" />
            </div>

            <form action={formAction} className="space-y-6">
              <input type="hidden" name="next" value={next} />
              <input type="hidden" name="ref" value={ref} />

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  autoComplete="name"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-blue focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-blue focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-blue focus:outline-none transition-colors"
                />
              </div>

              {state?.error ? (
                <p className="text-sm text-red-400">{state.error}</p>
              ) : null}

              <button
                type="submit"
                className="w-full bg-cl-blue text-cl-bg font-semibold text-sm py-3 rounded-full hover:bg-cl-blue/90 transition-colors"
              >
                Create account
              </button>
            </form>
          </div>

          <p className="text-center text-sm text-cl-muted mt-8">
            Already have an account?{" "}
            <Link href="/login" className="text-cl-blue hover:underline">
              Sign in
            </Link>
          </p>

        </div>
      </main>
    </div>
  );
}

function CheckSeal() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className="mt-0.5 shrink-0"
    >
      <circle cx="10" cy="10" r="9" fill="#22c55e" fillOpacity="0.15" />
      <path
        d="M6 10.2l2.6 2.6L14 7.4"
        stroke="#22c55e"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Scoped cl-* token definitions (exact hex from Base44's compiled CSS).
const authTokens = `
.cl-scope { font-family: Inter, ui-sans-serif, system-ui, sans-serif; }
.cl-scope .font-display { font-family: var(--font-space-grotesk), "Space Grotesk", sans-serif; }
.cl-scope .font-mono { font-family: ui-monospace, "SF Mono", monospace; }
.cl-scope.bg-cl-bg, .cl-scope .bg-cl-bg { background-color: #080808; }
.cl-scope .bg-cl-surface { background-color: #0e0e0e; }
.cl-scope .bg-cl-blue { background-color: #3b82f6; }
.cl-scope .hover\\:bg-cl-blue\\/90:hover { background-color: rgba(59, 130, 246, 0.9); }
.cl-scope .text-cl-text { color: #f0f0f0; }
.cl-scope .text-cl-muted { color: #888888; }
.cl-scope .text-cl-blue { color: #3b82f6; }
.cl-scope .text-cl-bg { color: #080808; }
.cl-scope .text-cl-green { color: #22c55e; }
.cl-scope .bg-cl-green-soft { background-color: rgba(34, 197, 94, 0.08); }
.cl-scope .border-cl-green { border-color: rgba(34, 197, 94, 0.35); }
.cl-scope .border-cl-border { border-color: #1c1c1c; }
.cl-scope .focus\\:border-cl-blue:focus { border-color: #3b82f6; }
.cl-scope input::placeholder { color: rgba(136, 136, 136, 0.35); }
`;
